import { classes } from "polytype";
import * as R from "remeda";
import type { FrontendFragments } from "../../../model/marketplace/Extension";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  Money,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { File } from "../../file/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { Contributor } from "../Contributor/index.js";
import type { MarketplaceContext } from "../ExtensionInstance/index.js";
import { ExtensionInstance } from "../ExtensionInstance/index.js";
import { ExtensionAsset } from "./ExtensionAsset.js";
import type {
  ExtensionData,
  ExtensionListItemData,
  ExtensionListQueryData,
} from "./types.js";

export class Extension extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "extension",
    "extension",
  );

  public static ofId(id: string) {
    return new Extension(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.extension.find(id);

    if (data) {
      return new ExtensionDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const extension = await this.find(id);
    assertObjectFound(extension, "extension", id);
    return extension;
  });

  public findDetailed = provideReact(
    () => Extension.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ExtensionDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Extension.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ExtensionDetailed>>;

  public static query(query: ExtensionListQueryData = {}) {
    return new ExtensionListQuery(query);
  }
}

class ExtensionCommon extends classes(
  DataModel<ExtensionData | ExtensionListItemData>,
  Extension,
) {
  public readonly contributor: Contributor;
  public readonly name: string;
  public readonly description?: string;
  public readonly detailedDescriptionMarkdown?: string;
  public readonly subTitle?: string;
  public readonly context: MarketplaceContext;
  public readonly scopes: string[];
  public readonly logo?: File;
  public readonly externalUrl?: string;
  public readonly assets: ExtensionAsset[];
  public readonly amountOfInstances?: number;
  public readonly pricing?: Money;
  public readonly frontendFragments: FrontendFragments;

  public constructor(data: ExtensionData | ExtensionListItemData) {
    super([data], [data.id]);
    this.contributor = Contributor.ofId(data.contributorId);
    this.name = data.name;
    this.description = data.description;
    // todo: Sprache prüfen und entsprechend laden
    this.detailedDescriptionMarkdown = data.detailedDescriptions?.de.markdown;
    // todo: Sprache prüfen und entsprechend laden
    this.subTitle = data.subTitle?.de;
    this.context = data.context;
    this.scopes = data.scopes;
    this.logo = data.logoRefId ? File.ofId(data.logoRefId) : undefined;
    this.externalUrl = data.frontendComponents?.[0]?.url;
    this.assets = data.assets
      .sort((a, b) => b.index - a.index)
      .map((a) => new ExtensionAsset(a));
    this.amountOfInstances = this.data.statistics.amountOfInstances;
    if (data.pricing?.netPrice) {
      this.pricing = Money({ amount: data.pricing.netPrice, currency: "EUR" });
    }
    this.frontendFragments = this.normalizeFrontendFragments(
      data.frontendFragments,
    );
  }

  public async install(contextId: string) {
    const { id } = await config.behaviors.extensionInstance.create({
      extensionId: this.id,
      context: this.context,
      consentedScopes: this.scopes,
      contextId,
    });

    return new ExtensionInstance(id);
  }

  private normalizeFrontendFragments(
    fragments: FrontendFragments = {},
  ): FrontendFragments {
    return R.mapValues(fragments, (value) =>
      R.evolve(value, {
        additionalProperties: (val) =>
          R.mapValues(val, (v) => {
            if (typeof v === "string" && v.startsWith("{") && v.endsWith("}")) {
              return JSON.parse(v);
            }
            return v;
          }),
      }),
    );
  }
}

export class ExtensionDetailed extends classes(
  ExtensionCommon,
  DataModel<ExtensionData>,
) {
  public constructor(data: ExtensionData) {
    super([data], [data]);
  }
}

export class ExtensionListItem extends classes(
  ExtensionCommon,
  DataModel<ExtensionListItemData>,
) {
  public constructor(data: ExtensionListItemData) {
    super([data], [data]);
  }
}

export class ExtensionListQuery extends ListQueryModel<ExtensionListQueryData> {
  public constructor(query: ExtensionListQueryData = {}) {
    super(query);
  }

  public refine(query: ExtensionListQueryData) {
    return new ExtensionListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.extension.list(
      this.query,
    );

    return new ExtensionList(
      this.query,
      items.map((d) => new ExtensionListItem(d)),
      totalCount,
    );
  }, [this.queryId, "extension"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "extension"]);
}

export class ExtensionList extends classes(
  ExtensionListQuery,
  ListDataModel<ExtensionListItem>,
) {
  public constructor(
    query: ExtensionListQueryData,
    extensions: ExtensionListItem[],
    totalCount: number,
  ) {
    super([query], [extensions, totalCount]);
  }
}
